import {
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  Host,
  OnInit,
  Renderer2,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { GasFormControlValueObservable } from '@cikrf/gas-utils/decorators';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, skip, startWith, tap } from 'rxjs/operators';

import { GasDynamicBaseComponent } from '../../../core/gas-dynamic-base/gas-dynamic-base.component';
import { GasObjectFilterPipe } from '../../../utils/gas-object-filter-pipe/gas-object-filter.pipe';
import {
  GasClickOutsideDirective,
} from '../../../utils/click-outside-directive/gas-click-outside.directive';
import { GasSelect } from '../gas-select.namespace';
import { GasSelectComponent } from '../wrapper/gas-select.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'gas-select-dropdown',
  templateUrl: './gas-select-dropdown.component.html',
  styleUrls: ['./gas-select-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    GasClickOutsideDirective,
    GasObjectFilterPipe,
  ],
  host: {
    '[class._multi]': `multi === true`,
    '[class._single]': `multi === false`,
  },
})
export class GasSelectDropdownComponent extends GasDynamicBaseComponent<string | number> implements OnInit, OnDestroy {
  @GasFormControlValueObservable('form')
  public formTitle$!: Observable<string>;

  public multi = false;

  public clearable = false;

  public search: string | boolean;

  public gasSelect!: GasSelectComponent;

  public form = new FormControl();

  public onlySelected = false;

  public isShowOnlySelected$: Observable<boolean>;
  public isShowOnlySelectedSubject$ = new BehaviorSubject<boolean>(false);

  public multiOptionsFilter$: Observable<GasSelect.Option[]> = combineLatest([
    this.isShowOnlySelectedSubject$,
    this.formTitle$,
  ])
    .pipe(
      startWith(''),
      map(([isShow, text]: [boolean, string]) => {
        return this.gasObjectFilterPipe.transform(this.multiOptions, 'title', text);
      }),
    );

  public unselectedOptionsFilter$: Observable<GasSelect.Option[]> = combineLatest([
    this.isShowOnlySelectedSubject$,
    this.formTitle$,
  ])
    .pipe(
      startWith(''),
      map(([isShow, text]: [boolean, string]) => {
        return this.gasObjectFilterPipe.transform(this.unselectedOptions, 'title', text);
      }),
    );

  public optionsFilter$: Observable<GasSelect.Option[]> = combineLatest([
    this.isShowOnlySelectedSubject$,
    this.formTitle$,
  ])
    .pipe(
      startWith(''),
      map(([isShow, text]: [boolean, string]) => {
        return this.gasObjectFilterPipe.transform(this.options, 'title', text);
      }),
    );

  private searchAbleBreakpoint = 13;
  private showOnlySelectedClicked = false;

  public get options(): GasSelect.Option[] {
    return this.gasSelect.mappedOptions;
  }

  public get hasSeveral(): boolean {
    return this.activeOptions.length > 0 && this.activeOptions.length < this.options.length;
  }

  public get allSelected(): boolean {
    return this.activeOptions.length === this.options.length;
  }

  public get selectedOptions(): GasSelect.Option[] {
    return this.options.filter((option: GasSelect.Option) => this.isActive(option.value));
  }

  public get unselectedOptions(): GasSelect.Option[] {
    return this.options.filter((option: GasSelect.Option) => !this.isActive(option.value));
  }

  public get isSearch(): boolean {
    //если параметр search=true всегда показываем поиск
    if (this.search === true) {
      return true;
    }

    //если параметр search=false скрываем блок с поиском
    if (this.search === false) {
      return false;
    }

    // если параметр search не передавался и количество опций 14 или больше - показываем поиск
    return this.search === undefined && this.options.length > this.searchAbleBreakpoint;
  }

  public get multiOptions(): GasSelect.Option[] {
    return this.isShowOnlySelectedSubject$.getValue()
      ? this.selectedOptions
      : this.options;
  }

  public get activeOptions(): string[] | number[] {
    return this.gasSelect.activeOptions;
  }

  public get hasOptions(): boolean {
    return this.options && !this.gasSelect.loading;
  }

  public get isShowSelectedCheckbox(): boolean {
    return (this.activeOptions.length > 0 || this.showOnlySelectedClicked)
      && this.activeOptions.length < this.options.length;
  }

  constructor(
    public el: ElementRef,
    @Host() public gasClickOutsideDirective: GasClickOutsideDirective,
    public renderer: Renderer2,
    private changeDetectorRef: ChangeDetectorRef,
    private gasObjectFilterPipe: GasObjectFilterPipe<GasSelect.Option>,
  ) {
    super(el, gasClickOutsideDirective, renderer);

    this.gasClickOutsideDirective.handleClick$
      .pipe(
        skip(1),
        tap(() => this.closeDropdown()),
        untilDestroyed(this),
      ).subscribe();

    this.isShowOnlySelected$ = this.isShowOnlySelectedSubject$.asObservable();
  }

  public ngOnInit(): void {
    this.gasSelect.isOpenedDropdown = true;

    this.gasSelect.changeDetectorRef.detectChanges();

    this.gasSelect.changeSubject$.pipe(
      tap(() => this.isShowOnlySelectedSubject$.next(this.isShowOnlySelectedSubject$.value)),
      untilDestroyed(this),
    ).subscribe(() => this.changeDetectorRef.detectChanges());

    this.clickOutside$
      .pipe(
        untilDestroyed(this),
      )
      .subscribe(() => this.closeDropdown());

    this.gasSelect.closeEmit$
      .pipe(
        untilDestroyed(this),
      )
      .subscribe(() => this.closeDropdown());

    const selectElement: HTMLElement = this.gasSelect.elementRef.nativeElement;

    this.renderer.setStyle(this.el.nativeElement, 'width', `${selectElement.clientWidth}px`);
  }

  public activate(value: string | number): void {
    this.gasSelect.activateEmit$.emit(value);
  }

  public clearValue(): void {
    this.gasSelect.activateEmit$.emit(null);
  }

  public isActive(value: string | number): boolean {
    let isActive = false;

    this.activeOptions.forEach((option: string | number) => {
      if (option === value) {
        isActive = true;
      }
    });

    return isActive;
  }

  public apply(): void {
    this.gasSelect.closeEmit$.emit();
  }

  public reset(): void {
    this.gasSelect.resetEmit$.emit();
  }

  public toggleShowSelected(): void {
    this.showOnlySelectedClicked = true;
    this.isShowOnlySelectedSubject$.next(!this.isShowOnlySelectedSubject$.getValue());
  }

  public toggleSelectAll(): void {
    if (this.hasSeveral) {
      this.unselectedOptions.forEach(option => this.activate(option.value));
    } else {
      this.options.forEach(option => this.activate(option.value));
    }

    if (!this.allSelected) {
      this.isShowOnlySelectedSubject$.next(false);
    }
    this.gasSelect.allSelected.emit(this.allSelected);
  }

  public ngOnDestroy(): void {
    this.gasSelect.isOpenedDropdown = false;
  }

  private closeDropdown(): void {
    this.gasSelect.isOpenedDropdown = false;

    this.gasSelect.changeDetectorRef.detectChanges();

    this.close(null);
  }
}
